/**
* @license Apache-2.0
*
* Copyright (c) 2025 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

var isnanf = require( '@stdlib/math-base-assert-is-nanf' );
var f32 = require( '@stdlib/number-float64-base-to-float32' );
var isFiniteNumber = require( '@stdlib/math-base-assert-is-finitef' );
var FLOAT16_EPSILON = require( '@stdlib/constants-float16-eps' );
var FLOAT16_MAX = require( '@stdlib/constants-float16-max' );
var FLOAT16_MIN = require( '@stdlib/constants-float16-smallest-normal' );
var EPS = require( '@stdlib/constants-float32-eps' );
var PINF = require( '@stdlib/constants-float32-pinf' );
var absf = require( '@stdlib/math-base-special-absf' );


// VARIABLES //

var INVERSE_EPSILON = f32( 1.0 / EPS );


// FUNCTIONS //

/**
* Performs banker's rounding (round-half-to-even) via floating-point arithmetic.
*
* @private
* @param {number} n - input value
* @returns {number} rounded value
*/
function roundTiesToEven( n ) {
	return f32( f32( n + INVERSE_EPSILON ) - INVERSE_EPSILON );
}


// MAIN //

/**
* Converts a single-precision floating-point number to the nearest half-precision floating-point number.
*
* @param {number} x - single-precision floating-point number
* @returns {number} nearest half-precision floating-point number
*
* @example
* var float64ToFloat32 = require( '@stdlib/number-float64-base-to-float32' );
*
* var y = float32ToFloat16( float64ToFloat32( 1.337 ) );
* // returns 1.3369140625
*/
function float32ToFloat16( x ) {
	var res;
	var mod;
	var a;
	var s;

	x = f32( x );
	if ( x === 0.0 || isnanf( x ) || !isFiniteNumber( x ) ) {
		return x;
	}
	if ( x < 0.0 ) {
		s = f32( -1.0 );
	} else {
		s = f32( 1.0 );
	}
	mod = absf( x );
	if ( mod < FLOAT16_MIN ) {
		return f32( f32( f32( s * roundTiesToEven( f32( f32( mod / FLOAT16_MIN ) / FLOAT16_EPSILON ) ) ) * FLOAT16_MIN ) * FLOAT16_EPSILON ); // eslint-disable-line max-len
	}
	// Leverage Veltkamp's algorithm for splitting a number into two numbers to generate an approximation to `x` which fits in a smaller number of bits:
	a = f32( f32( 1.0 + f32( FLOAT16_EPSILON / EPS ) ) * mod );
	res = f32( a - f32( a - mod ) );

	if ( res > FLOAT16_MAX || isnanf( res ) ) {
		return f32( s * PINF );
	}
	return f32( s * res );
}


// EXPORTS //

module.exports = float32ToFloat16;
